import React from 'react'
import { Field, Form } from 'react-final-form'
import {FormattedMessage, useIntl} from 'react-intl'
import {areaMessages} from '../../../config/commonMessages'
import {
  areaInPrefectureList,
  areaList,
  CURRENT_LOCATION,
  getPrefecture,
  Locale,
  PrefectureType
} from '../../../config/constants'
import FaMapMarkerAlt from '../../../assets/icons/FaMapMarkerAlt.svg'
import CheckIn from '../../../assets/icons/vanshare/SignIn.svg'
import CheckOut from '../../../assets/icons/vanshare/SignOut.svg'
import { maxCalendarYear, thisYear } from '../../../helpers/dateUtil'
import DateField from '../../fields/custom/DateField'
import SlidersSolid from '../../../assets/icons/vanshare/SlidersSolid.svg'
import Search from '../../../assets/icons/vanshare/Search.svg'
import {initialArea} from '../../maps/mapsUtil'
import { ListMode } from './MapListNavbar'
import styles from '../../carOrStation/StationCarList.scss'
import { ISearchForm } from '../cars/BasicSearch'

export const SearchButton = ({onSearchOpen, isVan, hasIcon = true}: {
  onSearchOpen: () => any,
  isVan?: boolean,
  hasIcon?: boolean
}) => <button type="button"
              className={[
                'btn px-0 px-md-2 py-0 font-weight-medium',
                isVan ? 'btn-outline-van' : 'btn-outline-primary',
                hasIcon && styles.searchDetailButton
              ].filter(Boolean).join(' ')}
              onClick={onSearchOpen}>
  {hasIcon && <SlidersSolid className={['mb-1 mr-2 fa', isVan ? 'text-van' : 'text-primary', styles.icon].join(' ')}/>}
  <FormattedMessage id="cars.search" defaultMessage="Advanced search" />
</button>

const StationsSearchInput = ({
  mode,
  area,
  prefecture,
  locale,
  checkIn,
  checkOut,
  onSearchSubmit,
  openSearch
}: {
  mode: ListMode,
  area: string,
  prefecture?: PrefectureType,
  locale: Locale,
  checkIn: Date,
  checkOut: Date,
  onSearchSubmit: (searchForm: ISearchForm) => void,
  openSearch: () => void
}) => {
  const intl = useIntl()
  return <div className={[
    mode !== ListMode.map ? 'mb-2 py-4 px-3' : 'ml-10px mb-2 pt-3 pb-2 px-4',
    styles.searchForm
  ].join(' ')}>
    <Form onSubmit={onSearchSubmit} initialValues={{...initialArea(area, prefecture), checkIn, checkOut}}>
      {({handleSubmit}) => <>
        <form onSubmit={handleSubmit}
              className={styles.searchBox}>
          <div className={[
            'form-group d-flex flex-column font-weight-medium',
            mode !== ListMode.map ? 'flex-md-row align-items-md-end' : 'flex-lg-row align-items-lg-end',
            mode !== ListMode.map && 'my-0 mx-md-3 mx-2'
          ].filter(Boolean).join(' ')}>
            <div className={[
              mode !== ListMode.map
                ? 'd-md-flex flex-md-grow-1 align-items-md-end'
                : 'd-lg-flex flex-lg-grow-1 align-items-lg-end',
              'flex-grow-0'
            ].filter(Boolean).join(' ')}>
              <div className={[
                'mb-3',
                mode === ListMode.map ? 'mr-lg-4 mb-lg-0' : 'mr-md-4 mb-md-0',
                mode !== ListMode.map && 'flex-md-grow-1'
              ].filter(Boolean).join(' ')}>
                <div className={['mb-1'].join(' ')}>
                  <FaMapMarkerAlt className={['mb-1px mr-1 text-primary', styles.icon].join(' ')}/>
                  <FormattedMessage id="carsSearch.area"/>
                </div>
                <div className={['position-relative'].join(' ')}>
                  <Field component="select" name="area" className={[
                    'pl-3 custom-select font-weight-medium', styles.prefectureInput
                  ].join(' ')}>
                    <FormattedMessage id="stationsSearch.allCountry">
                      {message => <option value="">
                        {message}
                      </option>
                      }
                    </FormattedMessage>
                    <FormattedMessage id="stationsSearch.currentLocation">
                      {message => <option value={CURRENT_LOCATION}>
                        {message}
                      </option>}
                    </FormattedMessage>
                    {
                      areaList.flatMap(area => {
                        const prefectures = areaInPrefectureList[area].map(
                          prefecture => ({text: `　${getPrefecture(locale, prefecture)}`, value: prefecture})
                        )
                        return [{text: intl.formatMessage({...areaMessages[area]}), value: area}, ...prefectures]
                      }).map((x, i) => <option key={i} value={x.value}>
                          {x.text}
                        </option>
                      )
                    }
                  </Field>
                </div>
              </div>
              <div className={[
                'row mb-3 mx-0 align-items-end',
                mode === ListMode.map ? 'mb-lg-0' : 'mb-md-0',
                mode === ListMode.map && 'flex-md-grow-1'
              ].filter(Boolean).join(' ')}>
                <div className="flex-grow-0 w-50">
                  <div className={['mb-1px'].join(' ')}>
                    <CheckIn className={['mb-1 mr-1 text-primary', styles.icon].join(' ')}/>
                    <FormattedMessage id="stationsSearch.checkIn"/>
                  </div>
                  <Field
                    component={DateField}
                    name="checkIn"
                    className={['px-2', styles.checkIn].join(' ')}
                    isDisabled={(clientDate: Date) => clientDate.getTime() < new Date().setHours(0, 0, 0, 0)}
                    minYear={thisYear}
                    maxYear={maxCalendarYear}
                  />
                </div>

                <div className="flex-grow-0 w-50">
                  <div className={['mb-1px'].join(' ')}>
                    <CheckOut className={['mb-1 mr-1 text-primary', styles.icon].join(' ')}/>
                    <FormattedMessage id="stationsSearch.checkOut"/>
                  </div>
                  <Field
                    component={DateField}
                    name="checkOut"
                    className={['px-2', styles.checkOut].join(' ')}
                    alignRight
                    isDisabled={(clientDate: Date) => clientDate.getTime() < new Date().setHours(0, 0, 0, 0)}
                    minYear={thisYear}
                    maxYear={maxCalendarYear}
                  />
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-around">
              <div className={[
                'ml-0 mr-2',
                mode === ListMode.map ? 'ml-lg-4 mr-lg-0' : 'ml-md-4 mr-md-0',
                styles.inputForm,
                mode !== ListMode.map && styles.listMode,
                mode !== ListMode.map && styles.buttonList,
              ].filter(Boolean).join(' ')}>
                <SearchButton onSearchOpen={openSearch} hasIcon={mode !== ListMode.map}/>
              </div>
              <div className={[
                'ml-0 ml-2',
                mode === ListMode.map ? 'ml-lg-3 ml-lg-0 mr-n1' : 'ml-md-4 ml-md-0',
                styles.inputForm,
                mode !== ListMode.map && styles.listMode,
                mode !== ListMode.map && styles.buttonList,
              ].filter(Boolean).join(' ')}>
                <button type="submit" className={['btn btn-primary w-100 font-weight-medium'].join(' ')}>
                  <Search className="fa mb-1 mr-2"/>
                  <FormattedMessage id="navbar.search"/>
                </button>
              </div>
            </div>
          </div>
        </form>
      </>
      }
    </Form>
  </div>
}

StationsSearchInput.displayName = 'StationsSearchInput'
export default StationsSearchInput
